import { type ComponentPropsWithRef, useRef } from 'react';
import { type AriaButtonProps, mergeProps, useButton, useFocusRing } from 'react-aria';

import { clsx } from '../../lib/clsx';
import { mergeRefs } from '../../lib/mergeRefs';
import { Glyph } from '../common/Glyph';

import { useI18n } from '../../hooks/useI18nFormatters';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';
import styles from './CloseButton.module.css';

interface CloseButtonProps
  extends AriaButtonProps<'button'>, Pick<ComponentPropsWithRef<'button'>, 'ref' | 'style'> {
  className?: string;
  /** Accessible name of the button. Defaults to the localized "Close". */
  'aria-label'?: string;
}

/**
 * Round 32×32 close button with a cross glyph, e.g. for the top corner of a dialog.
 */
export function CloseButton(props: CloseButtonProps) {
  const { i18n } = useI18n();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const button = useButton(
    {
      ...props,
      'aria-label': props['aria-label'] ?? i18n.messageFormatter('common.close'),
    },
    buttonRef,
  );
  const focusRing = useFocusRing();

  return (
    <button
      {...mergeProps(button.buttonProps, focusRing.focusProps)}
      ref={mergeRefs([buttonRef, props.ref])}
      className={clsx(styles.root, props.className)}
      style={props.style}
      data-is-pressed={button.isPressed}
      data-is-focus-visible={focusRing.isFocusVisible}
    >
      <Glyph
        source={glyph.core.cross}
        size={theme.icon.size_small}
        color={'var(--module-glyph-color)'}
      />
    </button>
  );
}
